import { type FormEvent, useEffect, useState } from "react";
import { initialPasswordError } from "../../invite-flow.js";
import { AuthLayout } from "../components/AuthLayout";
import { getSupabaseClient } from "../lib/supabase";

type InviteState = "loading" | "ready" | "failure";

export function AcceptInvitePage() {
  const [state, setState] = useState<InviteState>("loading");
  const [fatalError, setFatalError] = useState("");
  const [formError, setFormError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void getSupabaseClient().auth.getUser().then(({ data, error }) => {
      if (!active) return;
      if (error || !data.user) {
        setFatalError(
          "De uitnodigingslink is ongeldig of verlopen. Vraag een nieuwe uitnodiging aan.",
        );
        setState("failure");
        return;
      }
      setState("ready");
    }).catch(() => {
      if (!active) return;
      setFatalError(
        "De uitnodigingslink is ongeldig of verlopen. Vraag een nieuwe uitnodiging aan.",
      );
      setState("failure");
    });
    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    const validationError = initialPasswordError(password, confirmation);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setBusy(true);
    const client = getSupabaseClient();
    try {
      const { error: passwordError } = await client.auth.updateUser({
        password,
      });
      if (passwordError) {
        setFormError(
          "Het wachtwoord kon niet worden ingesteld. Vraag zo nodig een nieuwe link aan.",
        );
        return;
      }

      const { error: acceptanceError } = await client.rpc(
        "accept_user_invitation",
      );
      if (acceptanceError) {
        setFormError(
          "Het wachtwoord is ingesteld, maar de projecttoegang is nog niet geactiveerd. Probeer opnieuw of neem contact op met beheer.",
        );
        return;
      }

      await client.auth.signOut({ scope: "local" });
      globalThis.location.assign("/login/?invitation=accepted");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      {state === "loading" && (
        <section>
          <h1>Uitnodiging controleren</h1>
          <p>Een ogenblik.</p>
        </section>
      )}
      {state === "failure" && (
        <section>
          <h1>Uitnodiging niet beschikbaar</h1>
          <p className="error" role="alert">{fatalError}</p>
        </section>
      )}
      {state === "ready" && (
        <section>
          <h1>Stel je wachtwoord in</h1>
          <p>
            Na bevestiging wordt je vooraf toegewezen projecttoegang geactiveerd.
          </p>
          <form onSubmit={handleSubmit}>
            <label htmlFor="invite-password">Nieuw wachtwoord</label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              disabled={busy}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <label htmlFor="invite-confirmation">Herhaal wachtwoord</label>
            <input
              id="invite-confirmation"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              disabled={busy}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <button type="submit" disabled={busy}>
              {busy ? "Accepteren..." : "Uitnodiging accepteren"}
            </button>
          </form>
          {formError && <p className="error" role="alert">{formError}</p>}
        </section>
      )}
    </AuthLayout>
  );
}
